import _isNil from "lodash/isNil";
import _get from "lodash/get";
import { getInstitution, getProduct } from './common/general-utils';
import { getNameWithFallbacks, getImageBySize } from '../utils';
/**
 * Get the name to display
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {string}                    The offer name
 */

export var getName = function getName(marketplaceEntity) {
  return getNameWithFallbacks(marketplaceEntity);
};
/**
 * Get the max APR %
 * (divided by 100 because marketplace uses a value like 1234 to represent 12.34%)
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {number}                    The max APR
 */

export var getAprMax = function getAprMax(marketplaceEntity) {
  var maxApr = _get(marketplaceEntity, 'details.max_apr.value');

  return _isNil(maxApr) ? maxApr : maxApr / 100;
};
/**
 * Get the min APR %
 * (divided by 100 because marketplace uses a value like 1234 to represent 12.34%)
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {number}                    The min APR
 */

export var getAprMin = function getAprMin(marketplaceEntity) {
  var minApr = _get(marketplaceEntity, 'details.min_apr.value');

  return _isNil(minApr) ? minApr : minApr / 100;
};
/**
 * Get the APR tooltip text
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {String}                    The APR tooltip text
 */

export var getAprTooltip = function getAprTooltip(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.apr_tooltip.value');
};
/**
 * Get the bottom line text.
 *
 * @param   {Object} marketplaceEntity   The marketplace entity
 * @return  {String}                     The bottom line value.
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.tagline.value');
};
/**
 * Gets the "cons" bullets
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {Array}                     The "cons" array
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.cons_bullets.value');
};
/**
 * Get the disclaimer
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {String}                    The disclaimer
 */

export var getDisclaimer = function getDisclaimer(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.disclaimer.value');
};
/**
 * Get the fees bullets
 *
 * @param   {Object} marketplaceEntity  The marketplaceEntity
 * @return  {Array}                     The fees bullets
 */

export var getFeesBullets = function getFeesBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.fees_bullets.value');
};
/**
 * Get the max FICO score
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {number}                    The max FICO score
 */

export var getFicoMax = function getFicoMax(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.max_fico.value');
};
/**
 * Get the min FICO score
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {number}                    The min FICO score
 */

export var getFicoMin = function getFicoMin(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_fico_displayed.value');
};
/**
 * Get the min FICO tooltip text
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {String}                    The min FICO tooltip text
 */

export var getFicoMinTooltip = function getFicoMinTooltip(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_fico_tooltip.value');
};
/**
 * Get the min FICO annotation text
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {String}                    The min FICO annotation text
 */

export var getFicoMinAnnotation = function getFicoMinAnnotation(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_fico_annotation.value');
};
/**
 * Gets the alt text for the associated image
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {string}                    Alt text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.image_alt_text.value') || getName(marketplaceEntity);
};
/*
 * Get the source URL of the associated image
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @param   {string} size               Desired size of image, from constants.IMAGE_SIZES
 * @return  {string}                    The URL of the image
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return getImageBySize(marketplaceEntity);
};
/**
 * Get the issuer
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {string}                    Issuer name
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'name');
};
/**
 * Get the key facts bullets for an offer
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {Array}                     Array of key facts
 */

export var getKeyFactsBullets = function getKeyFactsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.key_facts_bullets.value');
};
/**
 * Gets url to learn more page
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {String}                    url
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.review_link.value');
};
/**
 * Get the legacy ID
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {string}                    Legacy ID
 */

export var getLegacyId = function getLegacyId(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.legacy_id.value');
};
/**
 * Get the tooltip for the loan term.
 *
 * @param {Object} marketplaceEntity - The marketplaceEntity
 * @return {string} - The tooltip associated with loan terms.
 */

export var getLoanTermAnnotation = function getLoanTermAnnotation(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.loan_term_annotation.value');
};
/**
 * Get the maximum loan term in months
 *
 * @param   {Object} marketplaceEntity  The marketplaceEntity
 * @return  {number}                    The maximum loan terms
 */

export var getLoanTermMax = function getLoanTermMax(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.max_loan_term.value');
};
/**
 * Get the minimum loan term in months
 *
 * @param   {Object} marketplaceEntity  The marketplaceEntity
 * @return  {number}                    The minimum loan terms
 */

export var getLoanTermMin = function getLoanTermMin(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_loan_term.value');
};
/**
 * Get the monthly payment amount
 *
 * @param   {Object} marketplaceEntity  The marketplaceEntity
 * @return  {number}                    The monthly payment amount
 */

export var getMonthlyPayment = function getMonthlyPayment(marketplaceEntity) {
  return _get(marketplaceEntity, 'derived_details.monthly_payment.value');
};
/**
 * Gets the "pros" bullets
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {Array}                     The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.pros_bullets.value');
};
/**
 * Get the qualifcations bullets
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {Array}                     Array of qualifications
 */

export var getQualificationsBullets = function getQualificationsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.qualifications_bullets.value');
};
/**
 * Get the slug
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {String}                    The slug
 */

export var getSlug = function getSlug(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'external_id');
};
/**
 * Get the star rating
 *
 * @param   {Object} marketplaceEntity  The marketplace entity
 * @return  {number}                    The star rating
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.rating_stars.value');
};
/**
 * Get the star rating tooltip
 *
 * @return  {string}                    The star rating tooltip
 */

export var getStarRatingTooltip = function getStarRatingTooltip() {
  return "NerdWallet's ratings are determined by our editorial team. The scoring formula takes into account factors we consider to be consumer-friendly, including impact to credit score, rates and fees, customer experience and responsible lending practices.";
};
/**
 * Get time to fund value
 *
 * @param   {Object} marketplaceEntity  The marketplaceEntity
 * @return  {number}                    The time to fund
 */

export var getTimeToFund = function getTimeToFund(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.time_to_fund.value');
};